/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useContext } from "react";
//Packages
import axios from "axios";
import { Container, Row, Col, Button, Table } from "react-bootstrap";
import { Link, Navigate, useNavigate } from "react-router-dom";
//Google Tag Manager
import TagManager from "react-gtm-module";
//Common
import FadeIn from "../common/FadeIn";
//Components
import PayYourInvoce from "./page-parts/PayYourInvoice";
import EditYourInfo from "./page-parts/EditYourInfo";
import CustomerChangeEmail from "./page-parts/CustomerChangeEmail";
import ParticipantManageBlock from "./page-parts/ParticipantManageBlock";
import EditParticipant from "./page-parts/EditParticipant";
import AddParticipant from "./page-parts/AddParticipant";
//Context
import { BusinessContextConsumer } from "../BusinessContext";
//Auth
import { isAuthenticatedCustomer } from "./services/CustomerAuth"; //import auth
//Loading
import LoadingSpinner from "../LoadingSpinner";
//Images and Icons
import { MdModeEditOutline, MdCardMembership, MdOutlineCancel, MdAdd } from "react-icons/md";
//TimeRendering
import Moment from "react-moment";

const Dashboard = () => {
  // set state
  const [customer, setCustomer] = useState("");
  const [invoices, setInvoiceData] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState("");
  // Manage ViewMode
  const [viewMode, setViewMode] = useState("view"); // view, editCustomerDetails, addParticipant, participant
  //Manage page reloads
  const [updateKey, setUpdateKey] = useState(0);
  const prevUpdateKey = useRef();
  // Errors and Submit
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);
  // Context
  const BusinessContext = useContext(BusinessContextConsumer);
  const { currencySymbol } = BusinessContext;
  // Navigate
  const navigate = useNavigate();
  // Get the customer data
  useEffect(async () => {
    prevUpdateKey.current = updateKey; // Assign Refs Value to current load

    //API call for customer data
    const getCustomerData = await axios.get(`/api/customer/customer_general/customer_view`, {
      withCredentials: true,
    });
    // API call for Invoices
    const getInvoices = await axios.get(`/api/customer/customer_general/invoices_view`, {
      withCredentials: true,
    });

    TagManager.dataLayer({
      dataLayer: {
        event: "visitedDashboard",
      },
    });

    //Ensure the customer is redirected to signup if they log in without completing.
    const testParticipants = getCustomerData.data.Participants;
    const testCustomerID = getCustomerData.data.stripe_customer_id;
    if (!testCustomerID || testParticipants.length === 0) {
      return navigate("/signup");
    }

    setInvoiceData(getInvoices.data);
    return setCustomer(getCustomerData.data);
  }, [updateKey]); // Run useEffect when the updateKey changes

  // Logic
  const unpaidInvoice = invoices.filter((e) => e.status === "open" && e.attempted).length > 0; // Is there an unpaid invoice
  // Navigate to the interested Class if it exists
  if (sessionStorage.getItem("interestedClass")) {
    navigate(`/checkout/${sessionStorage.getItem("interestedClass").replace(/"/g, "")}`);
  }
// Navigate to the interested Block if it exists
  if (sessionStorage.getItem("interestedBlock")) {
    navigate(`/checkout-block/${sessionStorage.getItem("interestedBlock").replace(/"/g, "")}`);
  }

  // Dismiss Message
  const dismissMessages = () => {
    setSuccessMessage("");
    return setErrorMessage("");
  };

  if (customer !== "") {
    return (
      <FadeIn delay={350} duration={450}>
        <Container>
          {unpaidInvoice && (
            <PayYourInvoce
              invoices={invoices}
              currencySymbol={currencySymbol}
              setSubmitClicked={setSubmitClicked}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              submitClicked={submitClicked}
            />
          )}
          <Row>
            <Col>
              <h1 className='text-center mt-3 mb-3'>Your Dashboard</h1>
            </Col>
          </Row>
          {viewMode === "view" && (
            <>
              <InitialBlock
                customer={customer}
                setViewMode={setViewMode}
                setSelectedParticipant={setSelectedParticipant}
              />
              {invoices.length > 0 && (
                <InvoiceBlock currencySymbol={currencySymbol} invoices={invoices} />
              )}
            </>
          )}
          {viewMode === "participant" && selectedParticipant !== "" && (
            <ParticipantManageBlock
              customer={customer}
              selectedParticipant={selectedParticipant}
              setViewMode={setViewMode}
              setSubmitClicked={setSubmitClicked}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              submitClicked={submitClicked}
              setUpdateKey={setUpdateKey}
              updateKey={updateKey}
              errorMessage={errorMessage}
              successMessage={successMessage}
              dismissMessages={dismissMessages}
              setSelectedParticipant={setSelectedParticipant}
            />
          )}
          {viewMode === "editParticipant" && selectedParticipant !== "" && (
            <EditParticipant
              customer={customer}
              selectedParticipant={selectedParticipant}
              setViewMode={setViewMode}
              setSubmitClicked={setSubmitClicked}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              submitClicked={submitClicked}
              setUpdateKey={setUpdateKey}
              updateKey={updateKey}
              errorMessage={errorMessage}
              successMessage={successMessage}
              dismissMessages={dismissMessages}
              setSelectedParticipant={setSelectedParticipant}
            />
          )}
          {viewMode === "addParticipant" && (
            <FadeIn delay={250} duration={450}>
              <Row>
                <Col>
                  <div className='text-center mt-2 mb-2'>
                    <Button variant='secondary' onClick={() => setViewMode("view")}>
                      <MdOutlineCancel />
                      Return
                    </Button>
                  </div>
                  <AddParticipant
                    customer={customer}
                    setViewMode={setViewMode}
                    setSubmitClicked={setSubmitClicked}
                    setErrorMessage={setErrorMessage}
                    setSuccessMessage={setSuccessMessage}
                    submitClicked={submitClicked}
                    setUpdateKey={setUpdateKey}
                    updateKey={updateKey}
                    errorMessage={errorMessage}
                    successMessage={successMessage}
                    dismissMessages={dismissMessages}
                    isDashboard={true}
                  />
                </Col>
              </Row>
            </FadeIn>
          )}
          {viewMode === "editCustomerDetails" && (
            <>
              <EditYourInfo
                customer={customer}
                setViewMode={setViewMode}
                setSubmitClicked={setSubmitClicked}
                setErrorMessage={setErrorMessage}
                setSuccessMessage={setSuccessMessage}
                submitClicked={submitClicked}
                setUpdateKey={setUpdateKey}
                updateKey={updateKey}
                errorMessage={errorMessage}
                successMessage={successMessage}
                dismissMessages={dismissMessages}
              />
              <CustomerChangeEmail
                customer={customer}
                setViewMode={setViewMode}
                setSubmitClicked={setSubmitClicked}
                setErrorMessage={setErrorMessage}
                setSuccessMessage={setSuccessMessage}
                submitClicked={submitClicked}
                setUpdateKey={setUpdateKey}
                updateKey={updateKey}
                errorMessage={errorMessage}
                successMessage={successMessage}
                dismissMessages={dismissMessages}
              />
            </>
          )}
          <Row>
            <Col className='text-center'>
              <div className='shadow mt-4 p-3 border'>
                <h4>Looking to join classes?</h4>
                <p>You can join multiple classes in the schedule.</p>
                <Button variant='success' size='lg' as={Link} to={`/`}>
                  Join Classes <MdCardMembership />
                </Button>
              </div>
            </Col>
          </Row>
          {!isAuthenticatedCustomer() && <Navigate to='/login/' />}
        </Container>
      </FadeIn>
    );
  } else {
    return <><LoadingSpinner />{!isAuthenticatedCustomer() && <Navigate to='/login/' />}</>;
  }
};

const InitialBlock = (props) => {
  const customer = props.customer;
  const participants = props.customer.Participants;
  return (
    <Row className='align-items-center'>
      <Col className='text-center' sm='12' lg='8'>
        <div className='shadow mt-4 p-3 border'>
          <h3>{`${customer.First_Name} ${customer.Last_Name}`}</h3>
          <hr />
          <h5>Address</h5>
          {`${customer.Street_Address}, ${customer.City} ${customer.Postcode}`}
          <hr />
          <h5>Tel</h5>
          <a href={`tel:${customer.Contact_Number}`}>{customer.Contact_Number}</a>
          <hr />
          <h5>Email</h5>
          <a href={`mailto:${customer.email}`}>{customer.email}</a>
          <hr />
          <h5>Marketing</h5>
          Heard about: {customer.heardAbout} | Consent: {customer.marketingConsent}
          <hr />
          <Button
            variant='info'
            className='w-100 mt-2 mb-2'
            onClick={() => props.setViewMode("editCustomerDetails")}
          >
            Not Correct? <MdModeEditOutline />
          </Button>
        </div>
      </Col>
      <Col className='text-center mt-4 mt-lg-0' sm='12' lg='4'>
        <div className='shadow mt-4 p-3 border'>
          <h1>Participants</h1>
          <p>Click to expand/manage</p>
          {participants.map((participant) => (
            <Button
              variant='primary'
              key={participant._id}
              className='w-100 mt-2 mb-2'
              onClick={(e) => {
                props.setSelectedParticipant(participant._id, e);
                props.setViewMode("participant");
              }}
            >
              {`${participant.First_Name} ${participant.Last_Name}`}
            </Button>
          ))}
          <Button
            variant='warning'
            className='w-100 mt-2 mb-2'
            onClick={() => {
              props.setViewMode("addParticipant");
            }}
          >
            Add <MdAdd />
          </Button>
        </div>
      </Col>
    </Row>
  );
};

const InvoiceBlock = (props) => {
  const invoices = props.invoices;
  const currencySymbol = props.currencySymbol;
  return (
    <Row className='mt-4'>
      <Col>
        <div className='shadow mt-4 p-3 border text-center'>
          <h3>Your bills</h3>
          <p>
            You can view and download your previous six bills related to your bookings here. If
            for any reason your payment is unsuccessful, you can click &quot;View/Pay&quot; and make
            a payment manually. Ad hoc bills for payment, if you have any, may also show here.
          </p>
          <Table striped bordered hover responsive className='text-center'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>View/Pay</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <small>
                      <Moment unix format='YYYY/MM/DD'>
                        {invoice.created}
                      </Moment>
                    </small>
                  </td>
                  <td>
                    <small>
                      {currencySymbol + parseFloat(invoice.amount_due / 100).toFixed(2)} -{" "}
                      {invoice.status}
                    </small>
                  </td>
                  <td>
                    <a target='_blank' rel='noopener noreferrer' href={invoice.hosted_invoice_url}>
                      <button className='btn btn-sm btn-info'>View/Pay</button>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Col>
    </Row>
  );
};

export default Dashboard;
